import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Stack,
  Avatar,
  Button,
  Link,
  CircularProgress,
  Alert,
  IconButton,
  Skeleton
} from '@mui/material';
import {
  People,
  Group,
  School,
  LocationOn,
  TrendingUp,
  TrendingDown,
  Balance,
  Article,
  OpenInNew,
  Schedule,
  Person,
  TrendingFlat,
  ThumbUp,
  ThumbDown,
  Share,
  Bookmark
} from '@mui/icons-material';
import { formatNumber, formatPercentage } from '../utils/dataUtils';
import constituencyService from '../services/constituencyService';

// Profile Modal Component
export const ProfileModal = ({ data }) => {
  if (!data?.profile) return <Typography>No profile data available</Typography>;
  
  const { profile } = data;
  
  return (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Basic Information
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Constituency Code</Typography>
              <Typography variant="h6" fontWeight="bold">{profile.code}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">State</Typography>
              <Typography variant="h6">{profile.state}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Reserved Status</Typography>
              <Chip 
                label={profile.reservedStatus} 
                color={profile.reservedStatus === 'General' ? 'default' : 'primary'}
                variant="outlined"
              />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Boundaries</Typography>
              <Typography variant="body1">{profile.boundaries}</Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      
      {/* Voter Statistics */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Voter Statistics
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Total Voters</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {formatNumber(profile.voters.total)}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>Gender Distribution</Typography>
              <Box mb={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Male</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumber(profile.voters.male)} ({formatPercentage((profile.voters.male/profile.voters.total)*100, 0)})
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(profile.voters.male/profile.voters.total)*100} 
                  sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Female</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumber(profile.voters.female)} ({formatPercentage((profile.voters.female/profile.voters.total)*100, 0)})
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(profile.voters.female/profile.voters.total)*100} 
                  sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                  color="secondary"
                />
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {profile.voters.literacyRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Literacy Rate
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="info.main">
                    {profile.voters.genderRatio || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Women per 1000 men
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Demographics Modal Component
export const DemographicsModal = ({ data }) => {
  if (!data?.demographics) return <Typography>No demographic data available</Typography>;
  
  const { demographics } = data;
  
  return (
    <Grid container spacing={3}>
      {/* Caste Distribution */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Caste Distribution
          </Typography>
          <Stack spacing={2}>
            {Object.entries(demographics.caste)
              .sort(([,a], [,b]) => b - a)
              .map(([group, percentage]) => (
                <Box key={group}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{group}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={percentage} 
                    sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
          </Stack>
        </Paper>
      </Grid>
      
      {/* Age Groups */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Age Group Distribution
          </Typography>
          <Stack spacing={2}>
            {Object.entries(demographics.ageGroups)
              .sort(([,a], [,b]) => b - a)
              .map(([group, percentage]) => (
                <Box key={group}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{group} years</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={percentage} 
                    sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
                    color="secondary"
                  />
                </Box>
              ))}
          </Stack>
        </Paper>
      </Grid>
      
      {/* Gender Distribution */}
      {demographics.genderDistribution && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              Gender Distribution
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <People sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {demographics.genderDistribution.male}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Male</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Group sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {demographics.genderDistribution.female}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Female</Typography>
                </Box>
              </Grid>
              {demographics.genderDistribution.thirdGender > 0 && (
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Balance sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {demographics.genderDistribution.thirdGender}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Third Gender</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      )}
      
      {/* Migration Impact */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Migration Impact
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {demographics.migrationImpact}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Voting Data Modal Component
export const VotingModal = ({ data }) => {
  if (!data?.voting) return <Typography>No voting data available</Typography>;
  
  const { voting } = data;
  
  return (
    <Grid container spacing={3}>
      {/* Turnout Statistics */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Voter Turnout
          </Typography>
          <Stack spacing={3}>
            <Box textAlign="center">
              <Typography variant="h3" fontWeight="bold" color="primary">
                {voting.turnout.percentage}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overall Turnout
              </Typography>
            </Box>
            
            <Divider />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold">
                    {formatNumber(voting.turnout.male)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Male Voters</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold">
                    {formatNumber(voting.turnout.female)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Female Voters</Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box>
              <Typography variant="body2" color="text.secondary">Total Votes Cast</Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(voting.turnout.total)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      
      {/* Voting Method */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Voting Method Breakdown
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">EVM Votes</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatNumber(voting.votingMethod.evm)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(voting.votingMethod.evm / voting.votingMethod.total) * 100} 
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">Postal Votes</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatNumber(voting.votingMethod.postal)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(voting.votingMethod.postal / voting.votingMethod.total) * 100} 
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
                color="secondary"
              />
            </Box>
            
            <Divider />
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontWeight="bold">NOTA Votes</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {formatNumber(voting.nota)}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Results Modal Component with Enhanced Bihar 2020 Data
export const ResultsModal = ({ data }) => {
  const [detailedResults, setDetailedResults] = useState(null);
  const [candidateAnalysis, setCandidateAnalysis] = useState(null);
  const [votingAnalysis, setVotingAnalysis] = useState(null);
  const [partyPerformance, setPartyPerformance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const constituencyName = data?.profile?.name;

  useEffect(() => {
    if (constituencyName) {
      loadDetailedResults();
    }
  }, [constituencyName]);

  const loadDetailedResults = async () => {
    if (!constituencyName) return;
    
    setLoading(true);
    try {
      const [detailed, candidates, voting, party] = await Promise.all([
        constituencyService.getDetailedResults(constituencyName),
        constituencyService.getCandidateAnalysis(constituencyName),
        constituencyService.getVotingAnalysis(constituencyName),
        constituencyService.getPartyPerformance(constituencyName)
      ]);
      
      setDetailedResults(detailed);
      setCandidateAnalysis(candidates);
      setVotingAnalysis(voting);
      setPartyPerformance(party);
    } catch (error) {
      console.error('Failed to load detailed results:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!data?.results && !detailedResults) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Results not yet declared or data unavailable
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading detailed results...</Typography>
      </Box>
    );
  }

  const winner = detailedResults?.winner || data?.results?.winner;
  const runnerUp = detailedResults?.runner_up || data?.results?.runnerUp;
  const victoryMargin = detailedResults?.victory_margin || data?.results?.margin;

  const tabs = ['Overview', 'Candidates', 'Voting Analysis', 'Party Performance'];

  return (
    <Box>
      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Stack direction="row" spacing={1}>
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              variant={activeTab === index ? 'contained' : 'outlined'}
              onClick={() => setActiveTab(index)}
              size="small"
            >
              {tab}
            </Button>
          ))}
        </Stack>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Winner Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Election Winner - Bihar 2020
              </Typography>
              {winner && (
                <Card sx={{ mt: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 56, height: 56 }}>🏆</Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight="bold">
                          {winner.candidate_name}
                        </Typography>
                        <Typography variant="h6">
                          {winner.party}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">Votes Received</Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {formatNumber(winner.votes)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">Vote Share</Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {winner.vote_percentage?.toFixed(1)}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Paper>
          </Grid>
          
          {/* Runner-up and Margin */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Competition Analysis
              </Typography>
              
              {runnerUp && (
                <Card sx={{ mt: 2, mb: 3 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: 'grey.400', mr: 2, width: 48, height: 48 }}>🥈</Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {runnerUp.candidate_name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {runnerUp.party}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Votes</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {formatNumber(runnerUp.votes)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Vote Share</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {runnerUp.vote_percentage?.toFixed(1)}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
              
              {victoryMargin && (
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">Victory Margin</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Vote Difference</Typography>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {formatNumber(victoryMargin.votes)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Margin %</Typography>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {victoryMargin.percentage?.toFixed(1)}%
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Turnout Summary */}
          {detailedResults?.turnout_summary && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                  Turnout & Voting Statistics
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {formatNumber(detailedResults.turnout_summary.total_votes)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Votes Polled
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {detailedResults.turnout_summary.turnout_percentage?.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Turnout Percentage
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {formatNumber(detailedResults.total_electors)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Electors
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {formatNumber(detailedResults.turnout_summary.postal_votes)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Postal Votes
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Candidates Tab */}
      {activeTab === 1 && candidateAnalysis && (
        <Grid container spacing={3}>
          {/* Candidate Statistics */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Candidate Statistics
              </Typography>
              <Stack spacing={2}>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {candidateAnalysis.total_candidates}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Candidates</Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Gender Breakdown</Typography>
                  {Object.entries(candidateAnalysis.gender_breakdown).map(([gender, count]) => (
                    <Box key={gender} display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body1">{gender}</Typography>
                      <Typography variant="body1" fontWeight="bold">{count}</Typography>
                    </Box>
                  ))}
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Age Statistics</Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Average Age</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {candidateAnalysis.age_statistics.average_age?.toFixed(1)} years
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Age Range</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {candidateAnalysis.age_statistics.min_age} - {candidateAnalysis.age_statistics.max_age}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Top Vote Getters */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Top Vote Getters
              </Typography>
              <List>
                {candidateAnalysis.vote_analysis.slice(0, 10).map((candidate, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#CD7F32' : 'grey.300',
                        color: index < 3 ? 'white' : 'text.primary',
                        fontWeight: 'bold'
                      }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {candidate.candidate_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {candidate.party}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="h6" fontWeight="bold">
                              {formatNumber(candidate.votes)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {candidate.vote_percentage?.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Age Group Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Age Group Distribution
              </Typography>
              {Object.entries(candidateAnalysis.age_statistics.age_groups).map(([ageGroup, count]) => (
                <Box key={ageGroup} mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1">{ageGroup} years</Typography>
                    <Typography variant="body1" fontWeight="bold">{count} candidates</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(count / candidateAnalysis.total_candidates) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Category Breakdown */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Category Breakdown
              </Typography>
              {Object.entries(candidateAnalysis.category_breakdown).map(([category, count]) => (
                <Box key={category} mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1">{category}</Typography>
                    <Typography variant="body1" fontWeight="bold">{count}</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(count / candidateAnalysis.total_candidates) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Voting Analysis Tab */}
      {activeTab === 2 && votingAnalysis && (
        <Grid container spacing={3}>
          {/* Voting Overview */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Voting Pattern Analysis
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {formatNumber(votingAnalysis.voting_statistics.total_valid_votes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Valid Votes
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {votingAnalysis.voting_statistics.turnout_percentage?.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Turnout
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {formatNumber(votingAnalysis.voting_statistics.total_general_votes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      General Votes
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {formatNumber(votingAnalysis.voting_statistics.total_postal_votes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Postal Votes
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Vote Share Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Vote Share Distribution
              </Typography>
              {Object.entries(votingAnalysis.vote_share_distribution).map(([range, count]) => (
                <Box key={range} mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1">{range}</Typography>
                    <Typography variant="body1" fontWeight="bold">{count} candidates</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={count > 0 ? (count / votingAnalysis.top_performers.length) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Top Performers List */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Top 5 Performers
              </Typography>
              <List>
                {votingAnalysis.top_performers.slice(0, 5).map((performer, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      <Chip 
                        label={performer.rank} 
                        color={index === 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={performer.candidate_name}
                      secondary={`${performer.party} • ${formatNumber(performer.votes)} votes (${performer.vote_percentage?.toFixed(1)}%)`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Party Performance Tab */}
      {activeTab === 3 && partyPerformance && (
        <Grid container spacing={3}>
          {/* Party Overview */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Party Contest Overview
              </Typography>
              <Stack spacing={2}>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {partyPerformance.total_parties}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Parties</Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Winner Party</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {partyPerformance.winner_party}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Dominant Party Vote Share</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {partyPerformance.summary.dominant_party_vote_share?.toFixed(1)}%
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Two-Party Vote Share</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {partyPerformance.summary.two_party_vote_share?.toFixed(1)}%
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Party Performance Table */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Party Performance Rankings
              </Typography>
              <List>
                {partyPerformance.party_performance.slice(0, 10).map((party, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: index === 0 ? 'primary.main' : 'grey.300',
                        color: index === 0 ? 'white' : 'text.primary'
                      }}>
                        {party.rank}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {party.party_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {party.total_candidates} candidate{party.total_candidates > 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="h6" fontWeight="bold">
                              {formatNumber(party.total_votes)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {party.vote_percentage?.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Major Parties Analysis */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Major Parties ( {'>'}5% Vote Share)
              </Typography>
              <Grid container spacing={3}>
                {partyPerformance.major_party_analysis.map((party, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {party.party_name}
                        </Typography>
                        <Box mb={2}>
                          <Typography variant="body2" color="text.secondary">Vote Share</Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {party.vote_percentage?.toFixed(1)}%
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Box>
                            <Typography variant="body2" color="text.secondary">Votes</Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {formatNumber(party.total_votes)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Candidates</Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {party.total_candidates}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// Candidates Modal Component  
export const CandidatesModal = ({ data }) => {
  if (!data?.candidates) return <Typography>No candidate data available</Typography>;
  
  const { candidates } = data;
  
  return (
    <Grid container spacing={3}>
      {/* Candidate Statistics */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Candidate Statistics
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">Nominations Filed</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {candidates.filed.total}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">Nominations Rejected</Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {candidates.rejected.total}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">Nominations Withdrawn</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {candidates.withdrawn.total}
                </Typography>
              </Box>
            </Box>
            
            <Divider />
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontWeight="bold">Actually Contested</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {candidates.contested.total}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Forfeited Deposit</Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {candidates.forfeitedDeposit.total}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      
      {/* Gender Breakdown */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Gender Breakdown (Contested)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {candidates.genderBreakdown.men}
                </Typography>
                <Typography variant="body2" color="text.secondary">Men</Typography>
                <Typography variant="caption" color="text.secondary">
                  {candidates.contested.total > 0 ? 
                    `${Math.round((candidates.genderBreakdown.men / candidates.contested.total) * 100)}%` : 
                    '0%'
                  }
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="secondary">
                  {candidates.genderBreakdown.women}
                </Typography>
                <Typography variant="body2" color="text.secondary">Women</Typography>
                <Typography variant="caption" color="text.secondary">
                  {candidates.contested.total > 0 ? 
                    `${Math.round((candidates.genderBreakdown.women / candidates.contested.total) * 100)}%` : 
                    '0%'
                  }
                </Typography>
              </Box>
            </Grid>
            {candidates.genderBreakdown.thirdGender > 0 && (
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight="bold" color="info">
                    {candidates.genderBreakdown.thirdGender}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Third Gender</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round((candidates.genderBreakdown.thirdGender / candidates.contested.total) * 100)}%
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
          
          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Women representation: {candidates.contested.total > 0 ? 
                `${Math.round((candidates.genderBreakdown.women / candidates.contested.total) * 100)}%` : 
                '0%'
              } of all candidates
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Infrastructure Modal Component
export const InfrastructureModal = ({ data }) => {
  if (!data?.infrastructure) return <Typography>No infrastructure data available</Typography>;
  
  const { infrastructure } = data;
  
  return (
    <Grid container spacing={3}>
      {/* Polling Stations */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Polling Infrastructure
          </Typography>
          <Stack spacing={3}>
            <Box textAlign="center">
              <Typography variant="h3" fontWeight="bold" color="primary">
                {infrastructure.pollingStations.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Polling Stations
              </Typography>
            </Box>
            
            <Divider />
            
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="secondary">
                {infrastructure.pollingStations.averageElectors}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Electors per Station
              </Typography>
            </Box>
            
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Station Coverage Analysis
              </Typography>
              <Typography variant="body1">
                {infrastructure.pollingStations.averageElectors < 700 ? 
                  'Good coverage - Lower electors per station' :
                  infrastructure.pollingStations.averageElectors < 800 ?
                  'Adequate coverage - Moderate electors per station' :
                  'High density - More electors per station'
                }
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>
      
      {/* Election Timeline */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Election Timeline
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <LocationOn color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Polling Date"
                secondary={infrastructure.electionDates.polling || 'Not available'}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <TrendingUp color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Counting Date"
                secondary={infrastructure.electionDates.counting || 'Not available'}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <School color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Result Declaration"
                secondary={infrastructure.electionDates.resultDeclaration || 'Not available'}
              />
            </ListItem>
          </List>
        </Paper>
      </Grid>
    </Grid>
  );
};

// News & Media Modal Component
export const NewsModal = ({ data }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      if (!data?.profile?.name) {
        setError('Constituency information not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const newsData = await constituencyService.fetchConstituencyNews(data.profile.name, 10);
        setNews(newsData);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news. Please try again later.');
        // Set fallback news
        setNews(constituencyService.getFallbackNews(data.profile.name));
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [data?.profile?.name]);

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'Positive': return '#4caf50';
      case 'Negative': return '#f44336';
      default: return '#ff9800';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'Positive': return <ThumbUp sx={{ fontSize: 16 }} />;
      case 'Negative': return <ThumbDown sx={{ fontSize: 16 }} />;
      default: return <TrendingFlat sx={{ fontSize: 16 }} />;
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleShareNews = (article) => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.description,
        url: article.url
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(article.url);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Loading News for {data?.profile?.name}...
        </Typography>
        <Stack spacing={2}>
          {[1, 2, 3].map((item) => (
            <Card key={item} sx={{ p: 2 }}>
              <Skeleton variant="text" width="80%" height={30} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="60%" height={20} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ p: 3, pb: 2, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
          <Article sx={{ mr: 1, verticalAlign: 'middle' }} />
          News & Media Coverage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Latest news and updates for {data?.profile?.name} constituency
        </Typography>
        
        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* News Articles */}
      <Box sx={{ px: 3, pb: 3 }}>
        {news.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Article sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No recent news found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              We couldn't find recent news articles for this constituency.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {news.map((article, index) => (
              <Card 
                key={article.id} 
                elevation={2}
                sx={{ 
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    elevation: 6,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Grid container>
                  {/* Article Image */}
                  {article.urlToImage && (
                    <Grid item xs={12} md={4}>
                      <Box
                        component="img"
                        src={article.urlToImage}
                        alt={article.title}
                        sx={{
                          width: '100%',
                          height: { xs: 200, md: '100%' },
                          objectFit: 'cover',
                          minHeight: 160
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Grid>
                  )}
                  
                  {/* Article Content */}
                  <Grid item xs={12} md={article.urlToImage ? 8 : 12}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Title */}
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        gutterBottom
                        sx={{ 
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {article.title}
                      </Typography>

                      {/* Description */}
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        paragraph
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          flex: 1
                        }}
                      >
                        {article.description}
                      </Typography>

                      {/* Metadata */}
                      <Box sx={{ mt: 'auto' }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          {/* Sentiment */}
                          <Chip
                            icon={getSentimentIcon(article.sentiment)}
                            label={article.sentiment}
                            size="small"
                            sx={{
                              bgcolor: getSentimentColor(article.sentiment),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                          
                          {/* Fallback indicator */}
                          {article.isFallback && (
                            <Chip
                              label="Sample"
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          )}
                          
                          {/* Relevance Score */}
                          {article.relevanceScore > 5 && (
                            <Chip
                              label="High Relevance"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Stack>

                        {/* Source and Date */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                              {article.source}
                              {article.author && ` • ${article.author}`}
                            </Typography>
                          </Box>
                          
                          <Typography variant="caption" color="text.secondary">
                            <Schedule sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                            {formatDate(article.publishedAt)}
                          </Typography>
                        </Stack>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          {!article.isFallback && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<OpenInNew />}
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ flex: 1 }}
                            >
                              Read Full Article
                            </Button>
                          )}
                          
                          <IconButton
                            size="small"
                            onClick={() => handleShareNews(article)}
                            sx={{ color: 'primary.main' }}
                          >
                            <Share />
                          </IconButton>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 3, pt: 0, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          News powered by NewsAPI • Updated in real-time
        </Typography>
      </Box>
    </Box>
  );
};

export default {
  ProfileModal,
  DemographicsModal,
  VotingModal,
  ResultsModal,
  CandidatesModal,
  InfrastructureModal,
  NewsModal
};
